import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Layers,
  Code,
  Sparkles,
  Send,
  ExternalLink,
  Database,
  ShieldCheck,
  CreditCard,
  MailCheck,
  BarChart3,
  Workflow,
} from "lucide-react";
import { EpicomDesignsLogo } from "@/components/brand/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SEO metadata used by scripts/prerender.mjs (via SEO_ROUTES in ssrEntry.jsx).
 * Website design is offered as a Service by the existing Epicom Systems Ltd.
 * LocalBusiness entity — Epicom Designs is a branded division, not a separate
 * business location, so provider references the site-wide @id.
 */
export const meta = {
  title: "Website Design Lethbridge & Southern Alberta | Epicom Designs",
  metaDescription:
    "Website design and development for Southern Alberta — Lethbridge, Cardston, Magrath and beyond. Marketing sites, portfolios, band pages and small web apps built modern, fast and easy to maintain by Epicom Designs, a service of Epicom Systems Ltd.",
  canonicalPath: "/designs",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/designs#service",
    "name": "Website Design and Development",
    "alternateName": "Epicom Designs",
    "serviceType": "Website design, development and small web application build",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Website design and development service offered by Epicom Systems Ltd. under the Epicom Designs brand. Marketing sites, portfolios, band pages, personal landing pages and small web apps for businesses across Southern Alberta including Lethbridge, Cardston and Magrath.",
    "url": "https://epicom.ca/designs",
  },
};

const DESIGN_HERO =
  "https://images.unsplash.com/photo-1634084462412-b54873c0a56d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHx3ZWIlMjBkZXNpZ24lMjBkYXJrJTIwbW9kZXxlbnwwfHx8fDE3NzgwMjIzMzR8MA&ixlib=rb-4.1.0&q=85";

/* ----- Theme palette (Editorial Cream · Cobalt · Coral) ----- */
const T = {
  bg: "#F5F1E8",           // warm cream page bg
  surface: "#FFFFFF",      // white card surfaces
  ink: "#15171C",          // near-black text
  muted: "#5A5F6B",        // muted body text
  border: "#E0DAC9",       // soft cream border
  cobalt: "#2B3FE3",       // primary electric cobalt
  cobaltHover: "#1F2EBF",
  coral: "#FF6B3D",        // secondary warm coral
  coralHover: "#E5572B",
  lime: "#D8E841",         // tertiary pop
};

const CAPABILITIES = [
  {
    icon: Layers,
    title: "Marketing Sites",
    body: "Fast, modern single- or multi-page sites that actually convert. SEO-friendly, mobile-first, clean to maintain.",
    swatch: T.cobalt,
  },
  {
    icon: Code,
    title: "Small Web Apps",
    body: "Contact forms, booking pages, simple dashboards, and custom integrations. Built on a stack that's easy to hand off.",
    swatch: T.coral,
  },
  {
    icon: Sparkles,
    title: "Brand & Identity",
    body: "Logo refresh, color systems, and consistent visual language — whether you're starting fresh or levelling up.",
    swatch: T.lime,
  },
];

const STACK = [
  {
    icon: Database,
    title: "Real Backends",
    body: "Every site is built on a proper FastAPI / Node backend with a real database — not a glorified static page.",
    swatch: T.cobalt,
  },
  {
    icon: ShieldCheck,
    title: "Accounts & Auth",
    body: "Customer logins, role-based access, password reset flows, social sign-on — built with security best practices.",
    swatch: T.coral,
  },
  {
    icon: CreditCard,
    title: "Payments & Billing",
    body: "Stripe checkout, subscriptions, invoices, refunds — accept money cleanly and never deal with PCI headaches.",
    swatch: T.lime,
  },
  {
    icon: MailCheck,
    title: "Email Automations",
    body: "Order confirmations, lead notifications, drip campaigns, password resets — emails delivered reliably from your domain.",
    swatch: T.cobalt,
  },
  {
    icon: BarChart3,
    title: "Custom Dashboards",
    body: "Admin panels, sales boards, inventory views, analytics — your business logic, visualized exactly how you want it.",
    swatch: T.coral,
  },
  {
    icon: Workflow,
    title: "Third-Party Integrations",
    body: "Calendars, SMS, CRMs, mapping APIs, AI services — your site connects cleanly to the tools you already use.",
    swatch: T.lime,
  },
];

const WORK_FALLBACK = [
  {
    title: "Rebel Angels",
    tag: "MARKETING SITE",
    year: "2026",
    img: "https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png",
    url: "https://rebelangels.ca",
    isLogo: true,
    bg: "#0a0a0a",
  },
  {
    title: "Epicom Systems",
    tag: "MARKETING SITE",
    year: "2026",
    img: "/brand/epicom-site-hero.jpg",
    url: "https://epicom.ca",
  },
  {
    title: "Magrath Ag Society",
    tag: "COMMUNITY SITE",
    year: "2026",
    img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=80",
  },
];

/** Custom section label for the cream theme (cobalt bar + ink text). */
function Label({ children, color = T.cobalt }) {
  return (
    <div className="inline-flex items-center gap-3">
      <span
        aria-hidden="true"
        className="inline-block w-8 h-[2px]"
        style={{ background: color }}
      />
      <span
        className="text-xs tracking-[0.22em] uppercase font-semibold"
        style={{ color, fontFamily: "'IBM Plex Mono', monospace" }}
      >
        {children}
      </span>
    </div>
  );
}

/** Wraps children with a scroll-reveal fade-up. Adds .is-visible when in viewport. */
function Reveal({ children, delay = 0, as: Tag = "div", className = "", ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("is-visible");
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const delayClass = delay ? ` delay-${delay}` : "";
  return (
    <Tag
      ref={ref}
      className={`designs-reveal${delayClass} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default function Designs() {
  const [form, setForm] = useState({ name: "", email: "", project: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [work, setWork] = useState(WORK_FALLBACK);

  // Fetch live project list from the API; fall back to seed list if backend is unreachable
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/designs/projects`);
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        // Normalize backend snake_case → JSX camelCase
        const normalized = data.map((d) => ({
          title: d.title,
          tag: d.tag,
          year: d.year,
          img: d.img,
          url: d.url || undefined,
          isLogo: !!d.is_logo,
          bg: d.bg || undefined,
        }));
        setWork(normalized);
      } catch (err) {
        // Fall back silently to WORK_FALLBACK — but log so we know if the API is down.
        console.warn("Could not fetch design projects, using fallback:", err?.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.title = meta.title;
    window.scrollTo(0, 0);
    document.body.classList.add("designs-theme");

    // Swap favicons to the cobalt+coral designs variant while on this page
    const FAVICONS = {
      "icon-ico": { rel: "icon", href: "/brand/designs-favicon.ico", sizes: "any" },
      "icon-32":  { rel: "icon", href: "/brand/designs-favicon-32.png", sizes: "32x32", type: "image/png" },
      "icon-16":  { rel: "icon", href: "/brand/designs-favicon-16.png", sizes: "16x16", type: "image/png" },
      "apple":    { rel: "apple-touch-icon", href: "/brand/designs-apple-touch-icon.png", sizes: "180x180" },
    };
    const originalLinks = [];
    Array.from(document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')).forEach((link) => {
      originalLinks.push({
        rel: link.getAttribute("rel"),
        href: link.getAttribute("href"),
        sizes: link.getAttribute("sizes"),
        type: link.getAttribute("type"),
      });
      link.parentNode.removeChild(link);
    });
    Object.values(FAVICONS).forEach((cfg) => {
      const link = document.createElement("link");
      link.rel = cfg.rel;
      link.href = cfg.href;
      if (cfg.sizes) link.setAttribute("sizes", cfg.sizes);
      if (cfg.type) link.type = cfg.type;
      document.head.appendChild(link);
    });

    return () => {
      document.body.classList.remove("designs-theme");
      // Remove the designs favicons
      Array.from(document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')).forEach((link) => {
        link.parentNode.removeChild(link);
      });
      // Restore originals
      originalLinks.forEach((cfg) => {
        const link = document.createElement("link");
        link.rel = cfg.rel;
        if (cfg.href) link.href = cfg.href;
        if (cfg.sizes) link.setAttribute("sizes", cfg.sizes);
        if (cfg.type) link.type = cfg.type;
        document.head.appendChild(link);
      });
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      form.project.trim().length < 5
    ) {
      toast.error("Please fill name, email, and a short project description.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: "",
        service_type: "Web Design / Epicom Designs",
        message: form.project,
        source: "epicom-designs",
      });
      setSent(true);
      toast.success("Inquiry received — we'll be in touch shortly.");
      setForm({ name: "", email: "", project: "" });
    } catch {
      toast.error("Something went wrong. Please email designs@epicom.ca directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="designs-page"
      className="min-h-screen"
      style={{ background: T.bg, color: T.ink }}
    >
      {/* Top accent stripe — three-color signature, gently flowing */}
      <div className="h-[3px] w-full designs-stripe-flow" />

      {/* Header */}
      <header
        className="sticky top-0 z-40"
        style={{
          background: "rgba(245, 241, 232, 0.92)",
          backdropFilter: "saturate(140%) blur(8px)",
          WebkitBackdropFilter: "saturate(140%) blur(8px)",
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <nav className="max-w-[1300px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              data-testid="designs-logo-top"
              aria-label="Back to top"
              className="bg-transparent border-0 p-0 cursor-pointer"
            >
              <EpicomDesignsLogo
                variant="cream"
                className="h-9 w-auto"
              />
            </button>
          </div>
          <div className="flex items-center gap-5">
            <a
              href="#designs-work"
              className="hidden sm:inline text-xs tracking-[0.2em] uppercase font-semibold hover:opacity-70 transition-opacity"
              style={{ color: T.ink, fontFamily: "'IBM Plex Mono', monospace" }}
            >
              Work
            </a>
            <a
              href="#designs-capabilities"
              className="hidden sm:inline text-xs tracking-[0.2em] uppercase font-semibold hover:opacity-70 transition-opacity"
              style={{ color: T.ink, fontFamily: "'IBM Plex Mono', monospace" }}
            >
              Capabilities
            </a>
            <a
              href="#designs-contact"
              data-testid="designs-cta-inquire"
              className="inline-flex items-center gap-2 font-bold uppercase tracking-wider text-sm px-5 py-3 transition-all hover:translate-y-[-1px]"
              style={{
                background: T.cobalt,
                color: "#fff",
                boxShadow: `4px 4px 0 0 ${T.coral}`,
              }}
            >
              Start a Project
            </a>
          </div>
        </nav>
      </header>

      {/* HERO ——————————————————————————————————————————— */}
      <section
        data-testid="designs-hero"
        className="relative overflow-hidden"
        style={{ background: T.bg }}
      >
        {/* Decorative blob backgrounds for color pop */}
        <div
          aria-hidden="true"
          className="designs-blob-1 absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-90"
          style={{
            background: `radial-gradient(circle at center, ${T.coral}40 0%, transparent 70%)`,
            filter: "blur(40px)",
          }}
        />
        <div
          aria-hidden="true"
          className="designs-blob-2 absolute top-40 -left-20 w-[400px] h-[400px] rounded-full pointer-events-none opacity-90"
          style={{
            background: `radial-gradient(circle at center, ${T.cobalt}33 0%, transparent 70%)`,
            filter: "blur(40px)",
          }}
        />

        <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <Reveal>
                <Label>Web Design · Lethbridge & Southern Alberta</Label>
              </Reveal>
              <Reveal delay={1}>
                <h1
                  data-testid="designs-heading"
                  className="mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tight"
                  style={{ fontFamily: "'Archivo Black', sans-serif", color: T.ink }}
                >
                  Sites that{" "}
                  <span style={{ color: T.cobalt }} className="designs-underline">
                    actually
                  </span>
                  <br />
                  ship — and{" "}
                  <span
                    style={{
                      background: `linear-gradient(120deg, ${T.coral} 0%, ${T.coral} 60%, ${T.lime} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    perform.
                  </span>
                </h1>
              </Reveal>

              <Reveal delay={2}>
                <p
                  className="mt-8 text-lg md:text-xl leading-relaxed max-w-2xl"
                  style={{ color: T.muted }}
                >
                  Epicom Designs is a Southern Alberta web-design studio based
                  in the Lethbridge area. Marketing sites, band pages,
                  portfolios, personal landing pages, and small web apps — for
                  businesses, trades, makers, and anyone who needs a sharp
                  online home. Modern, fast, easy to maintain.
                </p>
              </Reveal>

              <Reveal delay={3} className="mt-10 flex flex-wrap gap-4 items-center">
                <a
                  href="#designs-contact"
                  data-testid="designs-hero-cta"
                  className="group inline-flex items-center gap-3 font-bold uppercase tracking-wider px-8 py-4 transition-all hover:translate-y-[-2px]"
                  style={{
                    background: T.cobalt,
                    color: "#fff",
                    boxShadow: `6px 6px 0 0 ${T.coral}`,
                  }}
                >
                  Start a Project
                  <ArrowRight
                    size={18}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
                <a
                  href="#designs-work"
                  className="inline-flex items-center gap-3 font-bold uppercase tracking-wider px-8 py-4 transition-all"
                  style={{
                    background: "transparent",
                    color: T.ink,
                    border: `2px solid ${T.ink}`,
                  }}
                >
                  See Work
                </a>
              </Reveal>
            </div>

            {/* Right side: stylized "browser" frame as visual */}
            <Reveal delay={4} className="lg:col-span-4 hidden lg:block">
              <div
                className="relative aspect-[4/5] rounded-md overflow-hidden"
                style={{
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  boxShadow: `12px 12px 0 0 ${T.cobalt}, 24px 24px 0 0 ${T.coral}`,
                }}
              >
                {/* fake browser top bar */}
                <div
                  className="flex items-center gap-2 px-4 py-3"
                  style={{ borderBottom: `1px solid ${T.border}` }}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF5F57" }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FEBC2E" }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#28C840" }} />
                </div>
                {/* abstract content (animated) */}
                <div className="p-6 space-y-4">
                  <div
                    className="h-2 designs-mockup-bar"
                    style={{ background: T.ink }}
                  />
                  <div
                    className="h-10 w-full designs-mockup-cta-bar"
                    style={{ background: T.cobalt }}
                  />
                  <div className="grid grid-cols-3 gap-2 mt-6">
                    <div className="designs-mockup-block-1 h-20" style={{ background: T.coral }} />
                    <div className="designs-mockup-block-2 h-20" style={{ background: T.lime }} />
                    <div className="designs-mockup-block-3 h-20" style={{ background: T.cobalt, opacity: 0.2 }} />
                  </div>
                  <div
                    className="h-2 mt-4 designs-mockup-bar-2"
                    style={{ background: T.muted }}
                  />
                  <div
                    className="h-2 designs-mockup-bar-3"
                    style={{ background: T.muted }}
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CAPABILITIES ——————————————————————————————————— */}
      <section
        id="designs-capabilities"
        className="relative py-20 md:py-28"
        style={{
          background: T.surface,
          borderTop: `1px solid ${T.border}`,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
          <Reveal>
            <Label color={T.coral}>What I Make</Label>
          </Reveal>
          <Reveal delay={1}>
            <h2
              className="mt-6 mb-14 text-4xl md:text-5xl font-black tracking-tight"
              style={{ fontFamily: "'Archivo Black', sans-serif" }}
            >
              Capabilities.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {CAPABILITIES.map((c, idx) => {
              const Icon = c.icon;
              return (
                <Reveal
                  key={c.title}
                  delay={idx + 1}
                  as="article"
                  data-testid={`designs-cap-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group relative p-8 transition-all hover:translate-y-[-4px]"
                  style={{
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                  }}
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{ background: c.swatch }}
                  />
                  <div
                    className="inline-flex items-center justify-center w-12 h-12 mb-6"
                    style={{ background: c.swatch }}
                  >
                    <Icon size={24} strokeWidth={2} color={T.ink} />
                  </div>
                  <h3
                    className="text-2xl font-black mb-3 tracking-tight"
                    style={{ fontFamily: "'Archivo Black', sans-serif" }}
                  >
                    {c.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: T.muted }}>
                    {c.body}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* UNDER THE HOOD ————————————————————————————— */}
      <section
        id="designs-under-hood"
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: T.bg }}
      >
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle at center, ${T.cobalt}26 0%, transparent 70%)`,
            filter: "blur(40px)",
          }}
        />

        <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <Reveal className="lg:col-span-7">
              <Label color={T.cobalt}>Under The Hood</Label>
              <h2
                className="mt-6 text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]"
                style={{ fontFamily: "'Archivo Black', sans-serif" }}
              >
                More than meets <br />
                the <span style={{ color: T.cobalt }}>eye.</span>
              </h2>
            </Reveal>
            <Reveal delay={1} className="lg:col-span-5">
              <p className="text-lg leading-relaxed" style={{ color: T.muted }}>
                Most "web design" stops at pretty pages. We build real
                applications. Behind every site is a proper backend with
                databases, accounts, payments, automations, and integrations —
                doing actual work for your business while your visitors just
                see a fast, polished site.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-[1px]" style={{ background: T.border }}>
            {STACK.map((s, idx) => {
              const Icon = s.icon;
              return (
                <Reveal
                  key={s.title}
                  delay={(idx % 3) + 1}
                  data-testid={`designs-stack-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="designs-stack-tile group relative p-8"
                  style={{ background: T.surface, "--swatch": s.swatch }}
                >
                  <div className="flex items-start gap-5">
                    <div
                      className="designs-stack-icon flex-shrink-0 inline-flex items-center justify-center w-11 h-11"
                      style={{ background: s.swatch }}
                    >
                      <Icon size={20} strokeWidth={2} color={T.ink} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className="designs-stack-title text-xl font-black mb-2 tracking-tight"
                        style={{ fontFamily: "'Archivo Black', sans-serif" }}
                      >
                        {s.title}
                      </h3>
                      <p className="text-sm leading-relaxed" style={{ color: T.muted }}>
                        {s.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Tech-stack ticker chip strip removed per user request */}
        </div>
      </section>

      {/* WORK ——————————————————————————————————————————— */}
      <section
        id="designs-work"
        className="py-20 md:py-28"
        style={{ background: T.bg }}
      >
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <Reveal>
              <Label color={T.cobalt}>Selected Work</Label>
              <h2
                className="mt-6 text-4xl md:text-5xl font-black tracking-tight"
                style={{ fontFamily: "'Archivo Black', sans-serif" }}
              >
                Recent Projects.
              </h2>
            </Reveal>
            <Reveal delay={1}>
              <span
                className="text-xs tracking-[0.2em] uppercase font-semibold"
                style={{ color: T.muted, fontFamily: "'IBM Plex Mono', monospace" }}
              >
                More on request →
              </span>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {work.map((w, idx) => {
              const Wrapper = w.url ? "a" : "article";
              const wrapperProps = w.url
                ? { href: w.url, target: "_blank", rel: "noopener noreferrer" }
                : {};
              const accent = [T.cobalt, T.coral, T.lime, T.cobalt][idx % 4];
              return (
                <Reveal
                  key={w.title}
                  delay={(idx % 3) + 1}
                  as={Wrapper}
                  data-testid={`designs-work-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group relative block transition-transform hover:translate-y-[-4px]"
                  style={{
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                  }}
                  {...wrapperProps}
                >
                  <div
                    className="relative aspect-[16/10] overflow-hidden"
                    style={{ background: w.bg || "#0a0a0a" }}
                  >
                    {w.img ? (
                      <img
                        src={w.img}
                        alt={w.title}
                        className={
                          w.isLogo
                            ? "absolute inset-0 w-full h-full object-contain p-12 transition-transform duration-700 group-hover:scale-105"
                            : "absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        }
                      />
                    ) : w.url ? (
                      // No image uploaded — use microlink.io to render a live thumbnail of the target URL.
                      // Free tier, no API key needed. Screenshot is cached for ~30 days.
                      <img
                        src={`https://api.microlink.io/?url=${encodeURIComponent(
                          w.url,
                        )}&screenshot=true&meta=false&embed=screenshot.url&viewport.width=1440&viewport.height=900&waitFor=1500`}
                        alt={w.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      // No image AND no URL — show a clean branded placeholder.
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center gap-3"
                        style={{ background: T.bg }}
                      >
                        <div
                          className="text-4xl font-bold tracking-tight"
                          style={{ color: accent, fontFamily: "'Fraunces', serif" }}
                        >
                          {w.title.charAt(0).toUpperCase()}
                        </div>
                        <div
                          className="text-[9px] tracking-[0.3em] uppercase"
                          style={{ color: T.muted, fontFamily: "'IBM Plex Mono', monospace" }}
                        >
                          Coming Soon
                        </div>
                      </div>
                    )}
                    {/* color pop: bottom corner bar */}
                    <div
                      className="absolute bottom-0 left-0 h-1.5 w-1/3 transition-all duration-500 group-hover:w-full"
                      style={{ background: accent }}
                    />
                  </div>
                  <div className="p-6 flex items-start justify-between gap-4">
                    <div>
                      <div
                        className="text-[10px] tracking-[0.25em] uppercase font-semibold mb-2"
                        style={{
                          color: accent,
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}
                      >
                        {w.tag} · {w.year}
                      </div>
                      <div
                        className="text-2xl font-black tracking-tight"
                        style={{ fontFamily: "'Archivo Black', sans-serif" }}
                      >
                        {w.title}
                      </div>
                      {w.url && (
                        <div className="text-sm mt-1" style={{ color: T.muted }}>
                          {w.url.replace(/^https?:\/\//, "")}
                        </div>
                      )}
                    </div>
                    {w.url && (
                      <ArrowUpRight
                        size={28}
                        className="flex-shrink-0 mt-1 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                        style={{ color: T.ink }}
                      />
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* LOCAL SEO ——————————————————————————————————— */}
      <section
        data-testid="designs-local-seo"
        className="relative py-20 md:py-28"
        style={{ background: T.bg }}
      >
        <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10">
          <Reveal>
            <Label>Southern Alberta</Label>
          </Reveal>
          <Reveal delay={1}>
            <h2
              className="mt-6 text-4xl sm:text-5xl md:text-6xl font-black leading-[0.95] tracking-tight max-w-3xl"
              style={{ fontFamily: "'Archivo Black', sans-serif", color: T.ink }}
            >
              Website design across{" "}
              <span style={{ color: T.cobalt }}>Southern Alberta.</span>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <div
              className="mt-8 space-y-5 max-w-3xl text-lg md:text-xl leading-relaxed"
              style={{ color: T.muted }}
            >
              <p>
                Epicom Designs is the website side of Epicom Systems Ltd. — a
                Southern Alberta company that has been running commercial
                networks and communications work across the region for years.
                The web-design work follows the same footprint: home base in
                the Lethbridge area, and clients from Cardston, Magrath and the
                surrounding communities right through to the rest of Southern
                Alberta.
              </p>
              <p>
                Most of the sites we build are for small and mid-size local
                businesses, trades, farms, non-profits and personal projects.
                Marketing sites, portfolios, band pages, small web apps.
                Everything is built to be fast, easy to update and honest about
                what a website can actually do — no template resellers, no
                lock-in.
              </p>
            </div>
          </Reveal>
        </div>
      </section>


      {/* INQUIRY FORM ——————————————————————————————————— */}
      <section
        id="designs-contact"
        className="relative py-20 md:py-28 overflow-hidden"
        style={{
          background: T.ink,
          color: T.bg,
        }}
      >
        <div
          aria-hidden="true"
          className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle at center, ${T.cobalt}50 0%, transparent 70%)`,
            filter: "blur(50px)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle at center, ${T.coral}40 0%, transparent 70%)`,
            filter: "blur(50px)",
          }}
        />

        <div className="relative max-w-[1000px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="inline-flex">
              <Label color={T.coral}>Project Inquiry</Label>
            </div>
            <h2
              className="mt-6 text-4xl md:text-5xl font-black tracking-tight"
              style={{ fontFamily: "'Archivo Black', sans-serif" }}
            >
              Got an idea?{" "}
              <span style={{ color: T.coral }}>Let's talk.</span>
            </h2>
            <p
              className="max-w-xl mx-auto mt-4"
              style={{ color: "#a8aab2" }}
            >
              Tell me a bit about your project. I'll reply within two business
              days with next steps or a scoped quote.
            </p>
          </div>

          {sent ? (
            <div
              data-testid="designs-success-state"
              className="p-10 text-center"
              style={{
                background: T.surface,
                color: T.ink,
                border: `2px solid ${T.coral}`,
              }}
            >
              <Label color={T.coral}>Inquiry Received</Label>
              <h3
                className="text-3xl font-black mt-4 mb-3 tracking-tight"
                style={{ fontFamily: "'Archivo Black', sans-serif" }}
              >
                Thanks.
              </h3>
              <p style={{ color: T.muted }}>
                I'll be in touch soon at the email you provided.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="designs-form"
              className="p-8 md:p-10 space-y-5"
              style={{
                background: T.surface,
                color: T.ink,
                border: `1px solid ${T.border}`,
              }}
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label
                    className="block mb-2 text-[10px] tracking-[0.25em] uppercase font-semibold"
                    style={{
                      color: T.muted,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                  >
                    Name *
                  </label>
                  <input
                    data-testid="designs-input-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full p-4 transition-all focus:outline-none"
                    style={{
                      background: T.bg,
                      color: T.ink,
                      border: `1px solid ${T.border}`,
                    }}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label
                    className="block mb-2 text-[10px] tracking-[0.25em] uppercase font-semibold"
                    style={{
                      color: T.muted,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                  >
                    Email *
                  </label>
                  <input
                    data-testid="designs-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    className="w-full p-4 transition-all focus:outline-none"
                    style={{
                      background: T.bg,
                      color: T.ink,
                      border: `1px solid ${T.border}`,
                    }}
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <div>
                <label
                  className="block mb-2 text-[10px] tracking-[0.25em] uppercase font-semibold"
                  style={{
                    color: T.muted,
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}
                >
                  Project *
                </label>
                <textarea
                  data-testid="designs-input-project"
                  required
                  rows={5}
                  value={form.project}
                  onChange={(e) =>
                    setForm({ ...form, project: e.target.value })
                  }
                  className="w-full p-4 transition-all focus:outline-none resize-none"
                  style={{
                    background: T.bg,
                    color: T.ink,
                    border: `1px solid ${T.border}`,
                  }}
                  placeholder="What are you trying to build?"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="designs-submit"
                  className="group inline-flex items-center gap-3 font-bold uppercase tracking-wider px-8 py-4 transition-all hover:translate-y-[-2px] disabled:opacity-50"
                  style={{
                    background: T.cobalt,
                    color: "#fff",
                    boxShadow: `5px 5px 0 0 ${T.coral}`,
                  }}
                >
                  {submitting ? "Sending…" : "Send Inquiry"}
                  {!submitting && <Send size={16} />}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-10"
        style={{ background: T.bg, borderTop: `1px solid ${T.border}` }}
      >
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10 flex items-center justify-center">
          <div
            className="text-xs tracking-[0.2em] uppercase font-semibold text-center"
            style={{ color: T.muted, fontFamily: "'IBM Plex Mono', monospace" }}
          >
            ©{" "}
            <Link
              to="/login"
              data-testid="designs-footer-secret-admin-link"
              aria-label="Admin"
              title=""
              className="select-none cursor-default"
              style={{ color: T.muted, textDecoration: "none", transition: "color 0.7s ease" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = T.coral)}
              onMouseLeave={(e) => (e.currentTarget.style.color = T.muted)}
            >
              {new Date().getFullYear()}
            </Link>{" "}
            Epicom Designs · All Rights Reserved
          </div>
        </div>
      </footer>
    </div>
  );
}
